import { FormFieldOptionDetails, FormFieldOptions } from "@tellescope/types-models";

/**
 * Resolves a field's selectable options to the richer `optionDetails` shape.
 *
 * Choice fields carry their options in one of two shapes: the legacy `choices` (plain strings) or
 * `optionDetails` (option plus an optional description). Both remain valid — fields authored before
 * `optionDetails` existed still only have `choices` — so anything rendering or validating choices must
 * accept either.
 *
 * When both are present, `choices` names the options and each one takes its description and
 * `showCondition` from the detail with the same `option`, falling back to the detail at the same
 * index. Legacy resolved the pair the same way (`hooks.tsx:1891`); without the positional fallback a
 * detail whose name drifted from its `choices` entry loses its `showCondition` and always shows.
 *
 * @param options - The field's `options` object, if any.
 * @returns Option details for the field, or an empty array when it defines no choices.
 */
export const resolveFieldOptionDetails = (options?: FormFieldOptions): FormFieldOptionDetails[] => {
  const { choices, optionDetails } = options ?? {};
  if (!optionDetails?.length) return choices?.map((option) => ({ option })) ?? [];
  if (!choices?.length) return optionDetails;

  return choices.map((option, index) => {
    const matched = optionDetails.find((detail) => detail.option === option) ?? optionDetails[index];
    return matched ? { ...matched, option } : { option };
  });
};
